import { Alert, Box, Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, PrimaryButton } from "../../../components/form";
import { APP_COLORS } from "../../../enums";
import { userForgotPassword } from "../../../api/auth";
import { useMutationWithDispatch } from "../../../hooks/useMutationWithDispatch";
import { ROUTE_PATHS } from "../../../routes/paths";
import {
  forgotPasswordSchema,
  ForgotPasswordFormData,
  PAGE_CONFIG,
} from "./config";
import { ForgotPasswordResponse, OutletContext } from "./types";

function ForgotPassword() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutateAsync: submitForgotPassword, isPending: submitting } =
    useMutationWithDispatch<ForgotPasswordResponse, ForgotPasswordFormData>(
      (payload) => userForgotPassword(payload)
    );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  const onSubmit = useCallback(
    async (data: ForgotPasswordFormData) => {
      try {
        const payload = {
          email: data.email.toLowerCase(),
        };

        const res = await submitForgotPassword(payload);

        if (res?.success) {
          navigate(ROUTE_PATHS.AUTH.LOGIN);
        }
      } catch (error) {
      }
    },
    [submitForgotPassword, navigate]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap={2}>
        <Box
          width="100%"
          mt={5}
          mb={2}
          display="flex"
          justifyContent="center"
        >
          <Alert severity="info">
            Enter your email address and we'll send you an email with
            instructions to reset your password
          </Alert>
        </Box>

        <FormTextField
          name="email"
          control={control}
          label="Email"
          required
          error={errors.email?.message}
          textFieldProps={{ type: "email" }}
        />

        <Stack gap={2} mt={5}>
          <PrimaryButton loading={submitting}>
            Request password reset
          </PrimaryButton>
          <Typography variant="medium" sx={{ textAlign: "center" }}>
            Don't Have an account,
            <Link
              to={ROUTE_PATHS.AUTH.SIGNUP}
              style={{ textDecoration: "none" }}
            >
              <Typography
                component="span"
                sx={{ ml: 1, cursor: "pointer", display: "inline" }}
                variant="body-ibmPlexMono-medium"
                color={APP_COLORS.secondaryDark}
              >
                Sign up
              </Typography>
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
}

export default ForgotPassword;
